"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { useMachineStore } from "../state/moodStore";

// Create a patch for the Next.js router to intercept navigation
let originalPush: Function | null = null;
let originalReplace: Function | null = null;

export function DirectNavigationGuard() {
  const router = useRouter();
  const pathname = usePathname();
  const previousPathname = useRef(pathname);

  const {
    binauralMachines,
    isochronicMachines,
    yStimMachines,
    noiseMachines,
    dualBinauralMachines,
    metronomes,
    samplers,
    turnEverythingOff,
  } = useMachineStore();

  // Check if any audio is playing
  const isAnyAudioPlaying =
    Object.values(binauralMachines).some((isPlaying) => isPlaying) ||
    Object.values(isochronicMachines).some((isPlaying) => isPlaying) ||
    Object.values(dualBinauralMachines).some((isPlaying) => isPlaying) ||
    Object.values(metronomes).some((isPlaying) => isPlaying) ||
    Object.values(samplers).some((isPlaying) => isPlaying) ||
    Object.values(yStimMachines || {}).some((isPlaying) => isPlaying) ||
    Object.values(noiseMachines || {}).some((isPlaying) => isPlaying);

  // Patch router methods to intercept programmatic navigation
  useEffect(() => {
    // Only patch once
    if (originalPush === null && router && typeof router.push === "function") {
      // Save originals
      originalPush = router.push as Function;
      originalReplace = router.replace as Function;

      // Replace with our patched version
      const patchedPush = function (this: any, url: string, options: any) {
        // Check if audio is playing
        const audioMachines = document.querySelector('[data-audio-playing="true"]');
        if (audioMachines || isAnyAudioPlaying) {
          console.log("Programmatic navigation detected - stopping audio");
          turnEverythingOff();
        }

        // Call original
        if (originalPush) {
          return originalPush.apply(this, [url, options]);
        }
      };

      const patchedReplace = function (this: any, url: string, options: any) {
        // Check if audio is playing
        if (isAnyAudioPlaying) {
          console.log("Programmatic replace navigation detected - stopping audio");
          turnEverythingOff();
        }

        // Call original
        if (originalReplace) {
          return originalReplace.apply(this, [url, options]);
        }
      };

      // Apply patches (this is hacky, but necessary for Next.js App Router)
      try {
        // @ts-ignore - this is a hack to patch the router
        router.push = patchedPush;
        // @ts-ignore - this is a hack to patch the router
        router.replace = patchedReplace;
      } catch (e) {
        console.error("Failed to patch router:", e);
      }
    }

    // Cleanup on unmount
    return () => {
      if (originalPush && router) {
        try {
          // @ts-ignore - restore original methods
          router.push = originalPush;
          // @ts-ignore - restore original methods
          router.replace = originalReplace;
        } catch (e) {
          console.error("Failed to restore router:", e);
        }
      }
    };
  }, [router, isAnyAudioPlaying, turnEverythingOff]);

  // Capture link clicks to stop audio before the navigation occurs
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      // Check if the clicked element is a link or has a link parent
      let element = e.target as HTMLElement | null;
      let iterations = 0; // Safety counter to prevent infinite loops

      // Traverse up to find if we clicked on a link or child of link
      while (element && iterations < 10) {
        if (
          element.tagName === "A" &&
          (element as HTMLAnchorElement).href &&
          !(element as HTMLAnchorElement).href.startsWith("javascript:")
        ) {
          // We found a link! If audio is playing, stop it
          if (isAnyAudioPlaying) {
            console.log("Link click detected - stopping audio");
            turnEverythingOff();
          }
          break;
        }
        element = element.parentElement;
        iterations++;
      }
    };

    // Add click listener to the document
    document.addEventListener("click", handleLinkClick);

    return () => {
      document.removeEventListener("click", handleLinkClick);
    };
  }, [isAnyAudioPlaying, turnEverythingOff]);

  // Setup handler for browser beforeunload event (page refresh/close)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAnyAudioPlaying) {
        console.log("Window beforeunload detected - stopping audio");
        turnEverythingOff();
      }
    };

    // Listen for the window beforeunload event
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Create a cleanup function to remove event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAnyAudioPlaying, turnEverythingOff]);

  // Track only actual pathname changes
  useEffect(() => {
    // Check if this is an actual navigation (pathname changed)
    const isNavigation = previousPathname.current !== pathname;

    if (isNavigation && isAnyAudioPlaying) {
      console.log(`Navigation detected: ${previousPathname.current} → ${pathname}`);
      console.log("Stopping audio due to navigation");
      turnEverythingOff();
    }

    // Update previous pathname
    previousPathname.current = pathname;
  }, [pathname, isAnyAudioPlaying, turnEverythingOff]);

  // Set a data attribute on the body to indicate if audio is playing
  useEffect(() => {
    if (typeof document !== "undefined") {
      if (isAnyAudioPlaying) {
        document.body.setAttribute("data-audio-playing", "true");
      } else {
        document.body.removeAttribute("data-audio-playing");
      }
    }
  }, [isAnyAudioPlaying]);

  return null; // This component doesn't render anything
}
